/**
 * ParticleConfig — shared constants, math helpers, the global quality
 * cap, and the `props → emitter config` normaliser for the platformer
 * particle stack. Split out of ParticleSystem.js (file-size rule):
 *
 *   ParticleConfig.js   ← you are here (data/config layer)
 *   ParticleShapes.js   geometry tessellation (look)
 *   ParticlePhysics.js  collision / interaction / pile motion
 *   ParticleSystem.js   the pooled emitter class (orchestration)
 *
 * Everything stays importable from ParticleSystem.js (barrel re-export)
 * so existing call sites don't change.
 */

export const num = (v, d) => (Number.isFinite(v) ? v : d);
export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a, b, t) => a + (b - a) * t;
export const DEG = Math.PI / 180;

/** Base size the geometry is tessellated at; per-frame we scale from this. */
export const BASE = 16;

/**
 * How fast resting (piled) flakes EASE toward their column target each
 * second — used when the pile settles after a death or compacts under
 * weight. Exponential approach: the flake covers ~`ACCUM_SETTLE`·dt of the
 * remaining gap per frame, so the heap melts/sinks smoothly instead of
 * teleporting a slot down. Low = slow "melting snow"; high = snappy.
 */
export const ACCUM_SETTLE = 5;

/**
 * Global particle-quality ceiling (a DEVICE-TIER cap, not a per-emitter
 * target). It bounds how many particles any single emitter may keep alive
 * at once, so a heavy effect can't blow the frame budget on weak hardware.
 * Because the per-emitter pool is lazy (it only allocates nodes as they're
 * actually needed), raising this costs nothing for light effects — it just
 * lets dense effects (snow, long-lived streams) go further on capable
 * devices. Driven by the `shared.settings → particleQuality` setting, which
 * is applied at boot (src/main.js) and live from the Settings screen.
 */
export const PARTICLE_QUALITY_CAPS = Object.freeze({
    low: 2000,      // Raspberry Pi / low-end
    medium: 4000,   // mid phones / laptops
    high: 6000,     // desktop / modern phone
    ultra: 10000,   // high-end PC
});

let PARTICLE_HARD_CAP = PARTICLE_QUALITY_CAPS.high;

/** Set the ceiling from a named quality tier ('low'|'medium'|'high'|'ultra').
 *  Unknown tiers are ignored. Returns the resulting cap. */
export function setParticleQuality(tier) {
    const cap = PARTICLE_QUALITY_CAPS[tier];
    if (cap) PARTICLE_HARD_CAP = cap;
    return PARTICLE_HARD_CAP;
}

/** Set an explicit numeric ceiling (escape hatch / tests). */
export function setParticleHardCap(n) {
    if (Number.isFinite(n) && n > 0) PARTICLE_HARD_CAP = Math.round(n);
    return PARTICLE_HARD_CAP;
}

/** Current global particle ceiling. */
export function getParticleHardCap() { return PARTICLE_HARD_CAP; }

/** Parse a "#rrggbb" string (or accept an int) into a 24-bit int. */
export function hexToInt(hex, fallback) {
    if (typeof hex === 'number' && Number.isFinite(hex)) return hex;
    if (typeof hex === 'string') {
        const s = hex[0] === '#' ? hex.slice(1) : hex;
        const n = parseInt(s, 16);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}

/** Channel-wise RGB lerp between two 24-bit colours. */
export function lerpColor(a, b, t) {
    const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
    const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
    const r = Math.round(lerp(ar, br, t));
    const g = Math.round(lerp(ag, bg, t));
    const bl = Math.round(lerp(ab, bb, t));
    return (r << 16) | (g << 8) | bl;
}

/**
 * Festive multi-colour palette used by the 'confetti' engine when an
 * emitter has no authored palette. Mirrors the look of the in-game
 * confetti bursts (FroggerPickupBurst / SnakePortalConfetti) the
 * designer already knows from the balloon + snake games.
 */
const DEFAULT_CONFETTI_PALETTE = [
    0xef4444, 0xf97316, 0xfbbf24, 0x22c55e,
    0x06b6d4, 0x3b82f6, 0xa855f7, 0xec4899, 0xffffff,
];

/** Normalise a palette (array of "#rrggbb" strings / ints) into 24-bit
 *  ints, dropping any unparseable entries; falls back to the default. */
function parsePalette(pal) {
    if (Array.isArray(pal) && pal.length) {
        const out = [];
        for (const c of pal) {
            const n = hexToInt(c, null);
            if (n != null) out.push(n);
        }
        if (out.length) return out;
    }
    return DEFAULT_CONFETTI_PALETTE;
}

/**
 * Valid "Player interaction" modes (see ParticlePhysics.applyPlayerInteract).
 * Anything outside this set falls back to the legacy `wake` flag for
 * back-compat.
 */
export const PLAYER_INTERACT_MODES = new Set(['off', 'wake', 'push', 'attract', 'repel', 'swirl']);

/**
 * Map a stage entity's `props` bag into a normalised emitter config.
 * Exported so the editor preview + the runtime entity build configs
 * identically.
 */
export function emitterConfigFromProps(props = {}) {
    // `rate` (spawns/sec) and `lifetime` (sec alive) are INDEPENDENT knobs.
    // A continuous stream therefore keeps ≈ rate × lifetime particles alive
    // at once. The pool cap MUST cover that, otherwise the pool fills, new
    // spawns are refused, and nothing appears again until the first batch
    // dies — which looks like "emit once every `lifetime` seconds" (a pulse)
    // rather than a steady stream. So the working cap is never allowed below
    // that steady-state need: `maxParticles` only RAISES it (e.g. to allow a
    // bigger one-shot contact burst). To use FEWER particles, lower Rate or
    // Lifetime — not this cap. The hard ceiling is the global device-tier
    // Particle Quality cap (Raspberry Pi → 2000 … high-end PC → 10000),
    // guarding against runaway pools on weak hardware.
    const HARD_CAP = PARTICLE_HARD_CAP;
    const rate = num(props.rate, 24);
    const lifetime = num(props.lifetime, 1.2);
    const steady = Math.ceil(rate * Math.max(0.05, lifetime)) + 8;
    const userCap = clamp(num(props.maxParticles, 120), 1, HARD_CAP);
    const maxParticles = Math.round(Math.min(HARD_CAP, Math.max(userCap, steady)));
    return {
        // Render engine: 'normal' = shaped + tint-lerp particles;
        // 'confetti' = flat tumbling paper recoloured from `palette`,
        // matching the Frogger / Snake confetti look.
        engine:      props.engine === 'confetti' ? 'confetti' : 'normal',
        palette:     parsePalette(props.palette),
        rate,
        lifetime,
        speedMin:    num(props.speedMin, 20),
        speedMax:    num(props.speedMax, 60),
        angle:       num(props.angle, 0),
        spread:      num(props.spread, 40),
        gravity:     num(props.gravity, 40),
        // Animated forces (default 0 = inert, so existing emitters are unchanged).
        radialAccel:     num(props.radialAccel, 0),
        tangentialAccel: num(props.tangentialAccel, 0),
        damping:         num(props.damping, 0),
        spin:            num(props.spin, 0),
        // Turbulent displace (opt-in, default 0 = inert): a cheap evolving
        // flow field nudges each particle so streams wander / churn like
        // wind, smoke eddies, or drifting snow instead of moving in clean
        // arcs. `turbScale` = eddy frequency (bigger → smaller, tighter
        // swirls), `turbSpeed` = how fast the field churns over time.
        turbulence:  num(props.turbulence, 0),
        turbScale:   clamp(num(props.turbScale, 0.02), 0.0005, 0.5),
        turbSpeed:   num(props.turbSpeed, 1),
        // Collision (opt-in): 'off' keeps the emitter purely visual (no
        // cost). 'ground' sweeps the tile grid; 'ground+player' also
        // bounces off the live player AABB. `bounce` is restitution 0..1.
        collide:     (props.collide === 'ground' || props.collide === 'ground+player') ? props.collide : 'off',
        bounce:      clamp(num(props.bounce, 0.3), 0, 1),
        // Accumulation (opt-in, default off): instead of bouncing/settling,
        // a landed flake PARKS as a real resting particle and stacks on top
        // of the flakes already in its column — snow building up thickness,
        // ash, sand. Each flake lives out its `lifetime` then dies (oldest
        // first), and the pile settles, so it holds a roughly steady
        // thickness. `accumMax` caps the per-column pile height (px).
        accumulate:  props.accumulate === 'on' || props.accumulate === true,
        accumMax:    clamp(num(props.accumMax, 64), 1, 400),
        // Pile "weight" 0..1: how much flakes compress under the load of the
        // flakes resting on top of them. 0 = no squeeze (each flake keeps its
        // full height — a loose, fluffy heap). Higher = deeper flakes claim
        // less height, so the pile packs into a denser mass and visibly sinks
        // as more lands. Combined with the smooth settle, this reads like
        // snow compacting / slowly melting lower rather than a rigid tower.
        accumWeight: clamp(num(props.accumWeight, 0.3), 0, 1),
        // Player interaction (opt-in, default 'off'): a live body (the
        // player) reshapes nearby particles. Modes —
        //   'wake'    slipstream: drag toward the body's velocity (needs movement)
        //   'push'    bow-wave: shove outward, boosted by speed (needs movement)
        //   'attract' magnet: pull toward the body (works while still)
        //   'repel'   force-field: push away from the body (works while still)
        //   'swirl'   orbit: circulate around the body (works while still)
        // `interactStrength` = intensity, `interactRadius` = reach (px).
        // Reads legacy `wake`/`wakeRadius` for back-compat. Needs a body
        // provider; wake/push also require it to report velocity (vx/vy px/s).
        playerInteract:   PLAYER_INTERACT_MODES.has(props.playerInteract)
            ? props.playerInteract
            : (num(props.wake, 0) > 0 ? 'wake' : 'off'),
        interactStrength: clamp(num(props.interactStrength, num(props.wake, 8)), 0, 20),
        interactRadius:   clamp(num(props.interactRadius, num(props.wakeRadius, 80)), 4, 512),
        startColor:  hexToInt(props.startColor, 0xffd24a),
        endColor:    hexToInt(props.endColor, 0xff5a2a),
        startSize:   num(props.startSize, 10),
        endSize:     num(props.endSize, 2),
        // Per-particle size jitter, 0..1 (0 = every particle identical,
        // 1 = ±100%). Applied as a fixed multiplier picked at spawn, so a
        // particle keeps its relative size as it lerps start→end.
        sizeRandom:  clamp(num(props.sizeRandom, 0), 0, 1),
        startAlpha:  num(props.startAlpha, 1),
        endAlpha:    num(props.endAlpha, 0),
        shape:       props.shape || 'circle',
        // Image mode: render a textured PNG sprite (host injects the
        // Texture via setTexture) instead of a drawn shape. Truthy when
        // the emitter has a `texture` slot picked.
        image:       !!props.texture,
        blend:       props.blend || 'add',
        spawnW:      num(props.spawnW, 0),
        spawnH:      num(props.spawnH, 0),
        maxParticles,
    };
}
